import { Injectable } from '@angular/core';
import { SQL_KEYWORDS, SQL_FUNCTIONS } from '../models/sql-syntax.constants';

@Injectable({
  providedIn: 'root'
})
export class SyntaxHighlighterService {
  /**
   * Highlights SQL syntax with appropriate CSS classes
   * @param code The SQL code to highlight
   * @returns HTML string with highlighted syntax
   */
  highlightSql(code: string): string {
    let highlighted = code;

    // Highlight SQL keywords (blue)
    SQL_KEYWORDS.forEach(keyword => {
      const regex = new RegExp(`\\b${keyword}\\b`, 'gi');
      highlighted = highlighted.replace(regex, `<span class="sql-keyword">${keyword.toUpperCase()}</span>`);
    });

    // Highlight SQL functions (purple)
    SQL_FUNCTIONS.forEach(func => {
      const regex = new RegExp(`\\b${func}\\s*\\(`, 'gi');
      highlighted = highlighted.replace(regex, match => {
        return `<span class="sql-function">${func.toUpperCase()}</span>(`;
      });
    });

    // Highlight string literals (green)
    highlighted = this.highlightStrings(highlighted);

    // Highlight numbers (orange)
    highlighted = this.highlightNumbers(highlighted);

    // Highlight comments (grey/italic)
    highlighted = this.highlightComments(highlighted);

    return highlighted;
  }

  /**
   * Highlights string literals in code
   * @param code The code to process
   * @returns Code with highlighted strings
   */
  private highlightStrings(code: string): string {
    return code.replace(/'([^'\\]|\\.)*'/g, '<span class="sql-string">$&</span>');
  }

  /**
   * Highlights numeric values in code
   * @param code The code to process
   * @returns Code with highlighted numbers
   */
  private highlightNumbers(code: string): string {
    return code.replace(/\b\d+(?:\.\d+)?\b/g, '<span class="sql-number">$&</span>');
  }

  /**
   * Highlights comments in code (both single-line and multi-line)
   * @param code The code to process
   * @returns Code with highlighted comments
   */
  private highlightComments(code: string): string {
    const len = code.length;
    let i = 0;
    const out: string[] = [];

    while (i < len) {
      const ch = code[i];
      const next = i + 1 < len ? code[i + 1] : '';

      // single-line comment: -- ... (until newline or end)
      if (ch === '-' && next === '-') {
        const start = i;
        i += 2;
        // consume until newline (keep the newline out of the comment)
        while (i < len && code[i] !== '\n' && code[i] !== '\r') i++;
        const comment = code.slice(start, i);
        out.push(`<span class="sql-comment">${comment}</span>`);
        continue;
      }

      // multi-line comment: /* ... */
      if (ch === '/' && next === '*') {
        const start = i;
        i += 2;
        // find the closing */
        const close = code.indexOf('*/', i);
        if (close === -1) {
          // unterminated comment -> highlight to end
          const comment = code.slice(start);
          out.push(`<span class="sql-comment">${comment}</span>`);
          break;
        } else {
          // include '*/'
          const comment = code.slice(start, close + 2);
          out.push(`<span class="sql-comment">${comment}</span>`);
          i = close + 2;
          continue;
        }
      }

      // otherwise copy current character
      out.push(ch);
      i++;
    }

    return out.join('');
  }
}
